'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { yp, gp, ReadinessGate, bp, xp, Sp } from './collection-freeze-packet';
import {
  ProductionArtifact,
  ProductionIllustrativeBadge,
  ProductionBadge,
} from './production-primitives';
import { J, ef, $d } from './sell-market-animation';
import { ProductionIcon } from './deliverable-previews';
import { ShowcaseIcon, ProductRender } from './scene-primitives';
import { referenceImages } from './reference-brands';
import { referenceProducts, ue } from './reference-products';
export function ReadinessReview({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = e >= yp;
  return (
    <ProductionArtifact
      icon={'clipboard'}
      title={translateShowcaseNode(showcaseCopy, 'Production Readiness Review')}
      view={'Gates'}
      footer={
        <React.Fragment>
          <span className={'text-ink-3 truncate text-[13px]'}>
            {translateShowcaseNode(
              showcaseCopy,
              'Your team makes the release decision.'
            )}
          </span>
          <ProductionIllustrativeBadge className={'ml-auto'} />
        </React.Fragment>
      }
    >
      <div
        className={
          'grid gap-5 p-3 sm:p-5 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md:gap-6'
        }
      >
        <ul className={'flex flex-col'}>
          {translateShowcaseNode(
            showcaseCopy,
            gp.map((t, n) => (
              <ReadinessGate key={t.name} gate={t} index={n} t={e} />
            ))
          )}
        </ul>
        <div className={'flex min-w-0 flex-col gap-4'}>
          <p className={'label'}>
            {translateShowcaseNode(showcaseCopy, 'Verdict')}
          </p>
          <div
            role={'img'}
            aria-label={translateShowcaseNode(
              showcaseCopy,
              'Verdict: Conditional Go'
            )}
            className={
              'relative grid grid-cols-[0.6fr_1.3fr_0.7fr] rounded-full bg-[var(--tint)] p-1'
            }
          >
            <span
              aria-hidden={!0}
              {...J(e, yp)}
              className={
                'pd-pop pointer-events-none absolute top-1 bottom-1 rounded-full bg-[var(--panel)] shadow-[var(--shadow-minimal)]'
              }
              style={{
                left: `calc(${(0.6 / 2.6) * 100}% + 2px)`,
                width: 'calc(50% - 4px)',
              }}
            />
            {translateShowcaseNode(
              showcaseCopy,
              bp.map((e, n) => (
                <span
                  key={e}
                  className={`relative z-[1] flex h-9 items-center justify-center gap-1.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-300 ${t && n === xp ? 'text-[var(--pd-amber)]' : 'text-ink-3'}`}
                >
                  {translateShowcaseNode(
                    showcaseCopy,
                    n === xp ? (
                      <ProductionIcon
                        name={'circleAlert'}
                        on={t}
                        className={'size-4'}
                      />
                    ) : null
                  )}
                  {translateShowcaseNode(showcaseCopy, e)}
                </span>
              ))
            )}
          </div>
          <div className={'relative flex flex-col gap-2'}>
            <span
              {...J(e, yp + 250)}
              className={
                'pd-stamp pointer-events-none absolute -top-1 right-1 z-[1] rounded-[6px] px-2 py-1 text-[11px] font-semibold tracking-[0.08em] text-[var(--pd-amber)] uppercase shadow-[inset_0_0_0_1.5px_var(--pd-amber)]'
              }
            >
              {translateShowcaseNode(showcaseCopy, 'Conditional Go')}
            </span>
            <p
              {...J(e, yp + 400)}
              className={'pd-in text-ink text-[13.5px] font-medium'}
            >
              {translateShowcaseNode(
                showcaseCopy,
                'Two conditions before bulk'
              )}
            </p>
            <ol className={'flex flex-col gap-2'}>
              {translateShowcaseNode(
                showcaseCopy,
                Sp.map((t, n) => (
                  <li
                    key={t}
                    {...J(e, yp + 600 + n * 260)}
                    className={
                      'pd-pop flex gap-2.5 rounded-[10px] bg-[var(--pd-amber-wash)] p-2.5 text-[13px] leading-[1.45]'
                    }
                  >
                    <span
                      className={
                        'num mt-px font-mono text-[12px] text-[var(--pd-amber)]'
                      }
                    >
                      {translateShowcaseNode(showcaseCopy, n + 1)}
                    </span>
                    <span className={'text-ink-2'}>
                      {translateShowcaseNode(showcaseCopy, t)}
                    </span>
                  </li>
                ))
              )}
            </ol>
            <p
              {...J(e, yp + 1200)}
              className={
                'pd-in text-ink-3 mt-1 flex items-center gap-1.5 text-[12.5px]'
              }
            >
              <ShowcaseIcon name={'users'} className={'size-3.5'} />
              {translateShowcaseNode(
                showcaseCopy,
                'Sign-off stays with your production lead.'
              )}
            </p>
          </div>
        </div>
      </div>
    </ProductionArtifact>
  );
}
export const Tp = referenceImages.lisiere.pieces;
export const Ep = referenceProducts.filter(
  (e) => e.category === 'Knitwear' && e.role === 'Core'
);
export const Dp = new Set(
  Ep.map((e) => referenceProducts.findIndex((t) => t === e))
);
export const Op = (e) =>
  e.reduce((e, t) => (e === null || t.price < e.price ? t : e), null);
export const kp = Op(referenceProducts.filter((e) => e.role === 'Entry'));
export const Ap = Op(
  referenceProducts.filter((e) => e.category === 'Tailoring')
);
export const jp = ['none', 'one', 'two', 'three', 'four'];
export const Mp = (e) => `$${e.toLocaleString('en-US')}`;
export const Np = Math.round(
  referenceProducts.reduce((e, t) => e + t.price, 0) / referenceProducts.length
);
export const Pp = (e) => referenceProducts.filter((t) => t.role === e).length;
export const Fp = [
  {
    label: 'Direction',
    value: `${referenceProducts.length}/${referenceProducts.length} on direction`,
    target: ue.name,
    share: 1,
    tone: 'green',
    at: 1700,
  },
  {
    label: 'Mix',
    value: `Hero ${Pp('Hero')} · Core ${Pp('Core')} · Entry ${Pp('Entry')}`,
    target: 'Hero 1 \xB7 Core 3 \xB7 Entry 2',
    share: 0.72,
    tone: 'amber',
    at: 2300,
  },
  {
    label: 'Price',
    value: `Avg. ${Mp(Np)}`,
    target: kp ? `Entry from ${Mp(kp.price)}` : 'Entry price set',
    share: 0.9,
    tone: 'green',
    at: 2900,
  },
];
export const Ip = [
  {
    kind: 'Overlap',
    glyph: 'overlap',
    text: `${Ep.map((e) => e.style).join(' and ')}: ${jp[Ep.length] ?? Ep.length} core knits at ${Ep.map((e) => Mp(e.price)).join(' and ')}.`.replace(
      /^./,
      (e) => e.toUpperCase()
    ),
    at: 3500,
  },
  {
    kind: 'Gap',
    glyph: 'gap',
    text: Ap
      ? `No entry piece in tailoring; the lowest is the ${Ap.style} at ${Mp(Ap.price)}.`
      : 'No entry piece in tailoring.',
    at: 4200,
  },
];
export function ReviewedProduct({ index: e, t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let n = referenceProducts[e],
    r = Tp[e];
  if (!n || !r) return null;
  let i = ef(t, 150, 140, Tp.length) > e,
    a = Dp.has(e) && t >= 3500;
  return (
    <figure
      data-on={String(i)}
      className={'pd-pop relative m-0 flex min-w-0 flex-col gap-1.5'}
    >
      <span className={'relative block'}>
        <ProductRender
          render={r}
          alt={translateShowcaseNode(showcaseCopy, r.alt)}
          className={'aspect-square rounded-[10px]'}
        />
        <span
          aria-hidden={!0}
          className={`pointer-events-none absolute -inset-[3px] rounded-[13px] shadow-[0_0_0_1.5px_var(--pd-amber)] transition-opacity duration-300 ${a ? 'opacity-100' : 'opacity-0'}`}
        />
        <span
          data-on={String(a)}
          className={'pd-pop absolute top-1.5 left-1.5 max-sm:hidden'}
        >
          <ProductionBadge
            tone={'amber'}
            className={'bg-[var(--panel)] shadow-[var(--shadow-minimal)]'}
          >
            <ProductionIcon name={'overlap'} className={'size-3.5'} />
            {translateShowcaseNode(showcaseCopy, 'Overlap')}
          </ProductionBadge>
        </span>
      </span>
      <figcaption className={'flex min-w-0 flex-col px-0.5 leading-4'}>
        <span className={'text-ink text-[12.5px] font-medium sm:truncate'}>
          {translateShowcaseNode(showcaseCopy, n.style)}
        </span>
        <span className={'text-ink-4 truncate text-[12px] max-sm:hidden'}>
          {translateShowcaseNode(showcaseCopy, n.category)}
          {translateShowcaseNode(showcaseCopy, ' \xB7 ')}
          {translateShowcaseNode(showcaseCopy, n.role)}
        </span>
        <span className={'num text-ink-3 text-[12px]'}>
          {translateShowcaseNode(showcaseCopy, Mp(n.price))}
        </span>
      </figcaption>
    </figure>
  );
}
export function ReviewFlag({ row: e, t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let n = $d(t, e.at, 700) * e.share,
    r = t >= e.at;
  return (
    <li {...J(t, e.at - 200)} className={'pd-in flex flex-col gap-1.5 py-2.5'}>
      <div className={'flex items-center gap-2 text-[13px]'}>
        <span className={'text-ink font-medium'}>
          {translateShowcaseNode(showcaseCopy, e.label)}
        </span>
        <span className={'num text-ink-3 ml-auto truncate'}>
          {translateShowcaseNode(showcaseCopy, e.value)}
        </span>
        <span
          className={`grid size-4 shrink-0 place-items-center ${e.tone === 'green' ? 'text-[var(--pd-green)]' : 'text-[var(--pd-amber)]'}`}
        >
          <ProductionIcon
            name={e.tone === 'green' ? 'circleCheck' : 'circleAlert'}
            on={r && n >= e.share * 0.98}
            className={'size-4'}
          />
        </span>
      </div>
      <span
        className={
          'relative block h-1 overflow-hidden rounded-full bg-[var(--tint-2)]'
        }
      >
        <span
          className={`absolute inset-y-0 left-0 rounded-full ${e.tone === 'green' ? 'bg-[var(--pd-green)]' : 'bg-[var(--pd-amber)]'}`}
          style={{
            width: `${n * 100}%`,
          }}
        />
      </span>
      <span className={'text-ink-4 text-[12px] leading-4'}>
        {translateShowcaseNode(showcaseCopy, 'Target: ')}
        {translateShowcaseNode(showcaseCopy, e.target)}
      </span>
    </li>
  );
}
